/**
 * GenericSignatureFactory - Factory duy nhất cho TẤT CẢ providers
 *
 * Thay vì tạo factory riêng cho mỗi provider, chỉ cần factory này
 * kết hợp với config trong providerConfigs.ts
 *
 * THÊM PROVIDER MỚI:
 * 1. Thêm enum vào SignProvider
 * 2. Thêm config vào USB_PROVIDER_CONFIGS / HSM_PROVIDER_CONFIGS / CA_PROVIDER_CONFIGS
 * 3. DONE! Không cần tạo class mới
 */

import { ISignatureFactory } from '../ISignatureFactory';
import { ISignatureProvider } from '../signers/ISignatureProvider';
import { SignType, SignProvider, ProviderConfig } from '../types';
import {
  GenericUSBSigner,
  GenericHSMSigner,
  GenericCASigner,
  USB_PROVIDER_CONFIGS,
  HSM_PROVIDER_CONFIGS,
  CA_PROVIDER_CONFIGS,
  DEFAULT_SERVER_URLS,
} from '../signers/generic';
import { OceantechSimpleSigner } from '../signers/oceantech/OceantechSimpleSigner';

/**
 * Generic Signature Factory
 *
 * Một factory duy nhất có thể tạo signer cho BẤT KỲ provider nào
 * dựa trên config định nghĩa sẵn.
 */
export class GenericSignatureFactory implements ISignatureFactory {
  readonly provider: SignProvider;
  readonly displayName: string;
  readonly supportedTypes: SignType[];

  constructor(provider: SignProvider) {
    this.provider = provider;
    this.displayName = this.getDisplayName(provider);
    this.supportedTypes = this.getSupportedTypes(provider);
  }

  private getDisplayName(provider: SignProvider): string {
    const usbConfig = USB_PROVIDER_CONFIGS[provider];
    const hsmConfig = HSM_PROVIDER_CONFIGS[provider];
    const caConfig = CA_PROVIDER_CONFIGS[provider];
    return usbConfig?.displayName || hsmConfig?.displayName || caConfig?.displayName || provider;
  }

  private getSupportedTypes(provider: SignProvider): SignType[] {
    const types: SignType[] = [SignType.IMAGE]; // Luôn hỗ trợ IMAGE

    if (USB_PROVIDER_CONFIGS[provider]) types.push(SignType.USB);
    if (HSM_PROVIDER_CONFIGS[provider]) types.push(SignType.HSM);
    if (CA_PROVIDER_CONFIGS[provider]) types.push(SignType.CA);

    return types;
  }

  supportsSignType(signType: SignType): boolean {
    return this.supportedTypes.includes(signType);
  }

  createSigner(signType: SignType, config?: Partial<ProviderConfig>): ISignatureProvider {
    switch (signType) {
      case SignType.USB:
        return this.createUSBSigner(config);
      case SignType.HSM:
        return this.createHSMSigner(config);
      case SignType.CA:
        return this.createCASigner(config);
      case SignType.IMAGE:
        return this.createImageSigner(config);
      default:
        throw new Error(`Loại ký không xác định: ${signType}`);
    }
  }

  createUSBSigner(config?: Partial<ProviderConfig>): ISignatureProvider {
    const providerConfig = USB_PROVIDER_CONFIGS[this.provider];
    if (!providerConfig) {
      throw new Error(`${this.provider} không hỗ trợ USB Token`);
    }

    return new GenericUSBSigner({
      ...providerConfig,
      port: config?.port || providerConfig.port,
    });
  }

  createHSMSigner(config?: Partial<ProviderConfig>): ISignatureProvider {
    const providerConfig = HSM_PROVIDER_CONFIGS[this.provider];
    if (!providerConfig) {
      throw new Error(`${this.provider} không hỗ trợ HSM`);
    }

    const serverUrl = config?.domain || DEFAULT_SERVER_URLS[this.provider];
    if (!serverUrl) {
      throw new Error(`${this.provider} HSM yêu cầu cấu hình server URL`);
    }

    return new GenericHSMSigner({
      ...providerConfig,
      serverUrl,
      apiKey: config?.apiKey,
    });
  }

  createCASigner(config?: Partial<ProviderConfig>): ISignatureProvider {
    const providerConfig = CA_PROVIDER_CONFIGS[this.provider];
    if (!providerConfig) {
      throw new Error(`${this.provider} không hỗ trợ Remote CA`);
    }

    return new GenericCASigner({
      ...providerConfig,
      serverUrl: config?.domain || DEFAULT_SERVER_URLS[this.provider],
      clientId: config?.options?.clientId,
      clientSecret: config?.options?.clientSecret,
      redirectUrl: config?.options?.redirectUrl,
    });
  }

  private createImageSigner(config?: Partial<ProviderConfig>): ISignatureProvider {
    return new OceantechSimpleSigner(config?.options?.signatureImage);
  }
}
